import React, { useState, useMemo } from 'react';
import { useClaimsEnabled } from '../lib/features';
import { money } from '../lib/data';
import { useDenialPatterns } from '../api/hooks';
import { Bot, Zap, AlertTriangle, DollarSign, ArrowRight, X, Check } from 'lucide-react';
import { toast } from '../lib/aiToast';

// One inline number in the briefing sentence. Reads as emphasized text, behaves
// like a filter button — clicking it scopes the queue to exactly those rows.
function Facet({ value, tone, filter, active, onFilter, title }) {
  return (
    <button
      type="button"
      className="dh-facet"
      data-tone={tone || undefined}
      data-active={active ? 'true' : undefined}
      onClick={() => onFilter?.(filter)}
      title={title}
    >
      {value}
    </button>
  );
}

// The briefing bar: the day's agent-written status sentence plus the proactive
// findings. Every number in the sentence doubles as a click-to-filter facet, so
// the narrative and the navigation are the same object. The scannable KPI tiles
// live just below it on the Worklist page.
export default function Briefing({ practiceId = 'prac_001', name = 'there', summary, filter, onFilter }) {
  const claimsEnabled = useClaimsEnabled();
  const [applied, setApplied] = useState({});
  const [dismissed, setDismissed] = useState({});

  const { data: denialPatterns = [] } = useDenialPatterns(practiceId);

  // inactive / highDeductible / atStake all come from the scoped summary (computed
  // in Worklist over the same timeframe-filtered set the queue shows), so every
  // number in the briefing reflects the same day — not an all-time rollup.
  const inactive = summary?.inactive ?? 0;
  const highDeductible = summary?.highDeductible ?? 0;
  const atStake = summary?.atStake ?? 0;

  const topPattern = useMemo(
    () => (claimsEnabled ? [...denialPatterns].sort((a, b) => b.frequency - a.frequency)[0] : null),
    [denialPatterns, claimsEnabled],
  );

  const greeting = () => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'; };

  // Highest-priority first (inactive > recurring denial > HDHP), capped at two —
  // the one or two proactive things worth surfacing above the queue.
  const findings = [
    inactive > 0 && {
      id: 'inactive', icon: AlertTriangle, tone: 'text-rose-600',
      text: <>{inactive} patient{inactive > 1 ? 's have' : ' has'} <b>inactive coverage</b> — comms drafts are ready to send.</>,
      filter: 'inactive', actionLabel: 'Show these',
    },
    claimsEnabled && topPattern && {
      id: 'pattern', icon: Zap, tone: 'text-amber-600',
      text: <>Pattern: <b>{topPattern?.planName}</b> denied {topPattern?.frequency}× for “{topPattern?.denialReason}”. Auto-flag this plan + CPT at eligibility time?</>,
      rule: true,
    },
    highDeductible > 0 && {
      id: 'deductible', icon: DollarSign, tone: 'text-brand-600',
      text: <>{highDeductible} high-deductible (HDHP) visit{highDeductible > 1 ? 's' : ''} today with nothing met — flag front desk to <b>collect upfront</b>.</>,
      filter: 'hdhp', actionLabel: 'Show these',
    },
  ].filter(Boolean).filter((f) => !dismissed[f.id]).slice(0, 2);

  const total = summary?.total ?? 0;
  const autoCleared = summary?.autoCleared ?? 0;
  const needsAttention = summary?.needsAttention ?? 0;
  const high = summary?.high ?? 0;
  const copay = summary?.copayToCollect ?? 0;

  return (
    <div className="ai-surface dh-briefing-bar p-4 pl-5">
      <div className="flex items-start gap-3 min-w-0">
        <div className="h-9 w-9 rounded-lg bg-violet-600 text-white flex items-center justify-center shrink-0"><Bot className="h-5 w-5" /></div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-violet-600 font-semibold">Morning briefing · agent-written</div>
          {/* One sentence: what happened → what needs me → the money. Every number is a filter. */}
          <div className="text-sm text-slate-800 mt-1 leading-relaxed">
            {greeting()}, {name?.split(' ')[0]}. Agents verified{' '}
            <Facet value={total} filter="all" active={filter === 'all'} onFilter={onFilter} title="Show all appointments" />{' '}
            appointments and auto-cleared{' '}
            <Facet value={autoCleared} tone="cleared" filter="auto" active={filter === 'auto'} onFilter={onFilter} title="Show auto-cleared" />.{' '}
            <Facet value={needsAttention} tone="attention" filter="attention" active={filter === 'attention'} onFilter={onFilter} title="Show what needs you" />{' '}
            of {total} <b className="font-medium">need you</b>{' '}
            <span className="text-slate-300">·</span>{' '}
            <Facet value={high} tone="risk" filter="high" active={filter === 'high'} onFilter={onFilter} title="Show high-risk" /> high risk{' '}
            <span className="text-slate-300">·</span>{' '}
            <Facet value={money(copay)} tone="money" filter="copay" active={filter === 'copay'} onFilter={onFilter} title="Show copays to collect" /> to collect{' '}
            <span className="text-slate-300">—</span>{' '}
            <b className="tnum text-rose-600 font-semibold">{money(atStake)}</b> at stake.
          </div>
        </div>
      </div>

      {findings.length > 0 && (
        <div className="dh-findings">
          {findings.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.id} className="dh-finding">
                <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${f.tone}`} />
                <span className="flex-1 text-slate-700">{f.text}</span>
                {f.rule ? (
                  applied[f.id] ? (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-medium shrink-0"><Check className="h-3.5 w-3.5" />Rule created</span>
                  ) : (
                    <button onClick={() => { setApplied((a) => ({ ...a, [f.id]: true })); toast.success('Automation rule created — agents will act on it'); }} className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 bg-brand-50 border border-brand-100 rounded-lg px-2 py-1 hover:bg-brand-100 shrink-0">
                      <Zap className="h-3 w-3" />Create rule
                    </button>
                  )
                ) : (
                  <button onClick={() => onFilter?.(f.filter)} className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline shrink-0">{f.actionLabel}<ArrowRight className="h-3 w-3" /></button>
                )}
                <button onClick={() => setDismissed((d) => ({ ...d, [f.id]: true }))} className="text-slate-300 hover:text-slate-500 shrink-0" title="Dismiss"><X className="h-3.5 w-3.5" /></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
